import { PlanSchema, type Plan } from "@roxpacer/core";

export type PutResult = { status: "saved" } | { status: "conflict"; plan: Plan };

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
  }
}

export interface PlansApi {
  list(): Promise<Plan[]>;
  put(plan: Plan): Promise<PutResult>;
  delete(id: string): Promise<void>;
}

/** Returns a valid access token, or null when signed out. */
export type TokenProvider = () => Promise<string | null>;

export function createPlansApi(baseUrl: string, getToken: TokenProvider): PlansApi {
  const request = async (path: string, init: RequestInit = {}) => {
    const token = await getToken();
    if (!token) throw new ApiError(401, "Not signed in");
    const response = await fetch(`${baseUrl}${path}`, {
      ...init,
      headers: {
        ...init.headers,
        authorization: `Bearer ${token}`,
        ...(init.body ? { "content-type": "application/json" } : {}),
      },
    });
    const body = response.status === 204 ? undefined : await response.json().catch(() => ({}));
    return { status: response.status, body: body as Record<string, unknown> | undefined };
  };

  const fail = (status: number, body: Record<string, unknown> | undefined): never => {
    const message = typeof body?.error === "string" ? body.error : `Request failed (${status})`;
    throw new ApiError(status, message);
  };

  // Responses are validated like any other untrusted input.
  const parsePlan = (raw: unknown): Plan | null => {
    const result = PlanSchema.safeParse(raw);
    return result.success ? result.data : null;
  };

  return {
    async list() {
      const { status, body } = await request("/plans");
      if (status !== 200) fail(status, body);
      const raw = Array.isArray(body?.plans) ? body.plans : [];
      return raw.flatMap((p) => parsePlan(p) ?? []);
    },
    async put(plan) {
      const { status, body } = await request(`/plans/${encodeURIComponent(plan.id)}`, {
        method: "PUT",
        body: JSON.stringify(plan),
      });
      if (status === 200 || status === 201) return { status: "saved" };
      const current = status === 409 ? parsePlan(body?.plan) : null;
      if (current) return { status: "conflict", plan: current };
      return fail(status, body);
    },
    async delete(id) {
      const { status, body } = await request(`/plans/${encodeURIComponent(id)}`, {
        method: "DELETE",
      });
      if (status !== 204 && status !== 404) fail(status, body);
    },
  };
}
